import { Image, LoaderCircle, Play, RefreshCw, Trash2, UserRound } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { t } from '../i18n';
import { useMediaQuery } from '../hooks/use-media-query';
import type { AnimeSubject, Episode } from '../types/bgm';
import type { MediaResourcePlayResult, WatchLaterPage, WatchProgressInfo } from '../types/page';
import { formatDateTime } from '../utils/date';
import { safeUrl } from '../utils/url';
import { useApp } from './app-context';
import { Button } from './button';
import { CollectionTimeline } from './collection-timeline';
import { Divider } from './divider';
import { Drawer } from './drawer';
import { MediaSourceDialog } from './media-source-dialog';
import { TabBar, type TabBarItem } from './tab-bar';

type MyMenuTab = 'history' | 'watchLater' | 'collectionRecords';

interface HydratedEpisode {
  episode: Episode;
  subject: AnimeSubject;
}

interface HistoryItem extends HydratedEpisode {
  progress: WatchProgressInfo;
}

const menuIconButtonClass =
  'grid place-items-center text-(--muted) transition-colors hover:bg-(--surface) hover:text-(--text)';
const menuDangerIconButtonClass = 'grid place-items-center transition-colors hover:bg-[#e5636d]/10!';

const MenuState = ({
  children,
  emptyMessage,
  failed,
  loading,
  onRetry,
}: {
  children: ReactNode;
  emptyMessage: string;
  failed: boolean;
  loading: boolean;
  onRetry: () => void;
}) => {
  if (loading) {
    return (
      <div className="grid min-h-40 place-items-center text-(--muted)">
        <LoaderCircle className="size-5 spin" />
      </div>
    );
  }
  if (failed) {
    return (
      <div className="grid min-h-40 place-items-center gap-3 text-center text-xs text-(--muted)">
        <span>{t('requestFailedRetryLater')}</span>
        <Button compact onClick={onRetry}>
          <RefreshCw className="size-3.5!" />
          {t('refresh')}
        </Button>
      </div>
    );
  }
  return children || <div className="grid min-h-40 place-items-center text-xs text-(--muted)">{emptyMessage}</div>;
};

const MenuItem = ({
  actions,
  episode,
  meta,
  onClick,
  subject,
}: HydratedEpisode & { actions: ReactNode; meta?: string; onClick: () => void }) => {
  const image = safeUrl(subject.images?.large ?? subject.images?.common ?? subject.images?.medium);
  return (
    <article className="flex min-w-0 items-center gap-1 rounded-xl bg-(--surface-2) p-1.5">
      <Button
        variant="plain"
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-lg p-0.5 text-left transition-opacity hover:opacity-80 focus-visible:outline-0"
        onClick={onClick}
      >
        <span className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-lg bg-(--surface-2) text-(--muted) max-[480px]:hidden">
          {image ? (
            <img className="size-full object-cover" src={image} alt="" loading="lazy" referrerPolicy="no-referrer" />
          ) : (
            <Image className="size-4" />
          )}
        </span>
        <span className="grid min-w-0 flex-1 gap-1">
          <strong className="truncate text-xs text-(--text)">{subject.name_cn || subject.name}</strong>
          <small className="line-clamp-2 text-[10px] leading-4 text-(--muted)">
            EP.{episode.sort}
            {episode.name_cn || episode.name ? ` · ${episode.name_cn || episode.name}` : ''}
          </small>
          {meta ? <small className="text-[9px] text-(--input-accent)">{meta}</small> : null}
        </span>
      </Button>
      <div className="flex shrink-0 items-center gap-0.5">{actions}</div>
    </article>
  );
};

const HistoryTab = ({ close }: { close: () => void }) => {
  const { api, bgm, locale, navigate, showToast } = useApp();
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [revision, setRevision] = useState(0);
  const [deletingEpisodeId, setDeletingEpisodeId] = useState<number>();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    void api<WatchProgressInfo[]>('/api/watch-progress')
      .then(async (records) => {
        const subjects = new Map<number, Promise<AnimeSubject>>();
        const subjectFor = (subjectId: number): Promise<AnimeSubject> => {
          const existing = subjects.get(subjectId);
          if (existing) {
            return existing;
          }
          const request = bgm<AnimeSubject>(`/v0/subjects/${subjectId}`);
          subjects.set(subjectId, request);
          return request;
        };
        const hydrated = await Promise.allSettled(
          records.map(async (progress): Promise<HistoryItem> => ({
            progress,
            subject: await subjectFor(progress.bangumiSubjectId),
            episode: await bgm<Episode>(`/v0/episodes/${progress.bangumiEpisodeId}`),
          })),
        );
        if (active) {
          setItems(hydrated.flatMap((item) => (item.status === 'fulfilled' ? [item.value] : [])));
        }
      })
      .catch(() => {
        if (active) {
          setItems([]);
          setFailed(true);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [api, bgm, revision]);

  const remove = async (episodeId: number): Promise<void> => {
    setDeletingEpisodeId(episodeId);
    try {
      await api(`/api/watch-progress/${episodeId}`, { method: 'DELETE' });
      setItems((current) => current.filter((item) => item.episode.id !== episodeId));
      showToast(t('watchProgressDeleted'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : String(error), 'error');
    } finally {
      setDeletingEpisodeId(undefined);
    }
  };

  return (
    <MenuState
      emptyMessage={t('noWatchHistory')}
      failed={failed}
      loading={loading}
      onRetry={() => setRevision((current) => current + 1)}
    >
      {items.length ? (
        <div className="grid gap-1">
          {items.map(({ episode, progress, subject }) => {
            const completed = progress.duration > 0 && progress.progress / progress.duration >= 0.9;
            const percentage = Math.min(100, Math.round((progress.progress / progress.duration) * 100));
            const watchedAt = t('watchedAt').replace('{time}', formatDateTime(progress.updatedAt, locale));
            const progressMeta = completed
              ? `${t('finishedWatching')} · ${watchedAt}`
              : `${t('watchedPercent').replace('{percent}', String(percentage))} · ${watchedAt}`;
            const search = new URLSearchParams({
              download: progress.downloadUuid,
              episode: String(episode.ep ?? episode.sort),
              episodeId: String(episode.id),
              subject: String(subject.id),
              fileIndex: String(progress.fileIndex),
            });
            if (!completed) {
              search.set('position', String(Math.floor(progress.progress)));
            }
            return (
              <MenuItem
                episode={episode}
                subject={subject}
                meta={progressMeta}
                key={episode.id}
                onClick={() => {
                  close();
                  navigate(`/subjects/${subject.id}`);
                }}
                actions={
                  <>
                    <Button
                      variant="plain"
                      iconOnly
                      className={menuIconButtonClass}
                      aria-label={t('playMediaResource')}
                      title={t('playMediaResource')}
                      disabled={deletingEpisodeId !== undefined}
                      onClick={() => {
                        close();
                        navigate(`/playback?${search.toString()}`);
                      }}
                    >
                      <Play className="size-4!" />
                    </Button>
                    <Button
                      variant="plain"
                      iconOnly
                      color="danger"
                      className={menuDangerIconButtonClass}
                      aria-label={t('deleteWatchProgress')}
                      title={t('deleteWatchProgress')}
                      disabled={deletingEpisodeId !== undefined}
                      loading={deletingEpisodeId === episode.id}
                      onClick={() => void remove(episode.id)}
                    >
                      <Trash2 className="size-4!" />
                    </Button>
                  </>
                }
              />
            );
          })}
        </div>
      ) : null}
    </MenuState>
  );
};

const WatchLaterTab = ({ close, onPlay }: { close: () => void; onPlay: (item: HydratedEpisode) => void }) => {
  const { api, bgm, navigate, showToast } = useApp();
  const [items, setItems] = useState<HydratedEpisode[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [revision, setRevision] = useState(0);
  const [deletingEpisodeId, setDeletingEpisodeId] = useState<number>();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    void api<WatchLaterPage>('/api/watch-later?limit=12')
      .then(async (page) => {
        const subjects = new Map<number, Promise<AnimeSubject>>();
        const subjectFor = (subjectId: number): Promise<AnimeSubject> => {
          const existing = subjects.get(subjectId);
          if (existing) {
            return existing;
          }
          const request = bgm<AnimeSubject>(`/v0/subjects/${subjectId}`);
          subjects.set(subjectId, request);
          return request;
        };
        const hydrated = await Promise.allSettled(
          page.data.map(async (record): Promise<HydratedEpisode> => ({
            subject: await subjectFor(record.bangumiSubjectId),
            episode: await bgm<Episode>(`/v0/episodes/${record.bangumiEpisodeId}`),
          })),
        );
        if (active) {
          setItems(hydrated.flatMap((item) => (item.status === 'fulfilled' ? [item.value] : [])));
        }
      })
      .catch(() => {
        if (active) {
          setItems([]);
          setFailed(true);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [api, bgm, revision]);

  const remove = async (episodeId: number): Promise<void> => {
    setDeletingEpisodeId(episodeId);
    try {
      await api(`/api/watch-later/${episodeId}`, { method: 'DELETE' });
      setItems((current) => current.filter((item) => item.episode.id !== episodeId));
    } catch (error) {
      showToast(error instanceof Error ? error.message : String(error), 'error');
    } finally {
      setDeletingEpisodeId(undefined);
    }
  };

  return (
    <MenuState
      emptyMessage={t('noWatchLater')}
      failed={failed}
      loading={loading}
      onRetry={() => setRevision((current) => current + 1)}
    >
      {items.length ? (
        <div className="grid gap-1">
          {items.map((item) => (
            <MenuItem
              episode={item.episode}
              subject={item.subject}
              key={item.episode.id}
              onClick={() => {
                close();
                navigate(`/subjects/${item.subject.id}`);
              }}
              actions={
                <>
                  <Button
                    variant="plain"
                    iconOnly
                    className={menuIconButtonClass}
                    aria-label={t('playMediaResource')}
                    title={t('playMediaResource')}
                    disabled={deletingEpisodeId !== undefined}
                    onClick={() => onPlay(item)}
                  >
                    <Play className="size-4!" />
                  </Button>
                  <Button
                    variant="plain"
                    iconOnly
                    color="danger"
                    className={menuDangerIconButtonClass}
                    aria-label={t('removeWatchLater')}
                    title={t('removeWatchLater')}
                    disabled={deletingEpisodeId !== undefined}
                    loading={deletingEpisodeId === item.episode.id}
                    onClick={() => void remove(item.episode.id)}
                  >
                    <Trash2 className="size-4!" />
                  </Button>
                </>
              }
            />
          ))}
        </div>
      ) : null}
    </MenuState>
  );
};

export const MyMenu = () => {
  const { navigate } = useApp();
  const isMobile = useMediaQuery('(max-width: 700px)');
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<MyMenuTab>('history');
  const [contentRevision, setContentRevision] = useState(0);
  const [mediaItem, setMediaItem] = useState<HydratedEpisode>();
  const hostRef = useRef<HTMLDivElement>(null);
  const tabs: TabBarItem[] = [
    { value: 'history', label: t('watchHistory') },
    { value: 'watchLater', label: t('watchLater') },
    { value: 'collectionRecords', label: t('collectionRecords') },
  ];

  useEffect(() => {
    if (!open || isMobile) {
      return;
    }
    const closeFromPointer = (event: PointerEvent): void => {
      if (!hostRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const closeFromKeyboard = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', closeFromPointer);
    document.addEventListener('keydown', closeFromKeyboard);
    return () => {
      document.removeEventListener('pointerdown', closeFromPointer);
      document.removeEventListener('keydown', closeFromKeyboard);
    };
  }, [isMobile, open]);

  const renderMenuContent = (mobile: boolean): ReactNode => (
    <>
      <TabBar
        orientation={mobile ? 'horizontal' : 'vertical'}
        className={
          mobile
            ? 'w-full shrink-0 border-0! bg-transparent! p-0! [&_.tab-bar-item]:flex [&_.tab-bar-item]:min-h-9 [&_.tab-bar-item]:flex-1 [&_.tab-bar-item]:items-center [&_.tab-bar-item]:justify-center [&_.tab-bar-item]:px-2'
            : 'w-28 shrink-0 border-0! bg-transparent! p-0! [&_.tab-bar-item]:flex [&_.tab-bar-item]:min-h-9 [&_.tab-bar-item]:items-center [&_.tab-bar-item]:justify-start [&_.tab-bar-item]:px-3 [&_.tab-bar-item]:text-left'
        }
        items={tabs}
        value={tab}
        onChange={(value) => setTab(value as MyMenuTab)}
      />
      <Divider
        orientation={mobile ? 'horizontal' : 'vertical'}
        className={mobile ? 'my-3' : 'mx-3 h-auto! self-stretch'}
      />
      <div className="min-h-40 min-w-0 flex-1 overflow-y-auto">
        {tab === 'history' ? <HistoryTab key={`history-${contentRevision}`} close={() => setOpen(false)} /> : null}
        {tab === 'watchLater' ? (
          <WatchLaterTab
            key={`watch-later-${contentRevision}`}
            close={() => setOpen(false)}
            onPlay={(item) => {
              setOpen(false);
              setMediaItem(item);
            }}
          />
        ) : null}
        {tab === 'collectionRecords' ? (
          <CollectionTimeline
            key={`collection-records-${contentRevision}`}
            variant="menu"
            onNavigate={() => setOpen(false)}
          />
        ) : null}
      </div>
    </>
  );

  return (
    <>
      <div className="relative" ref={hostRef}>
        <Button
          variant="text"
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => {
            if (!open) {
              setContentRevision((current) => current + 1);
            }
            setOpen((current) => !current);
          }}
        >
          <UserRound />
          <span className="max-[1050px]:hidden">{t('my')}</span>
        </Button>
        {open && !isMobile ? (
          <section
            className="absolute top-[calc(100%+10px)] right-0 z-50 flex max-h-[min(620px,calc(100dvh-100px))] w-145 max-w-[calc(100vw-32px)] overflow-hidden rounded-2xl border border-(--line) bg-(--surface) p-3 shadow-2xl"
            role="dialog"
            aria-label={t('my')}
          >
            {renderMenuContent(false)}
          </section>
        ) : null}
      </div>
      {isMobile ? (
        <Drawer
          open={open}
          direction="top"
          ariaLabel={t('my')}
          panelClassName="max-h-[min(80dvh,620px)] flex-col p-3 pt-[max(12px,env(safe-area-inset-top))]"
          onClose={() => setOpen(false)}
        >
          {renderMenuContent(true)}
        </Drawer>
      ) : null}
      {mediaItem ? (
        <MediaSourceDialog
          subject={mediaItem.subject}
          episode={mediaItem.episode}
          onCancel={() => setMediaItem(undefined)}
          onPlay={(result: MediaResourcePlayResult, episode) => {
            const search = new URLSearchParams({
              download: result.download.uuid,
              episode: String(episode.ep ?? episode.sort),
              episodeId: String(episode.id),
              subject: String(mediaItem.subject.id),
              watchLater: '1',
            });
            setMediaItem(undefined);
            navigate(`/playback?${search.toString()}`);
          }}
        />
      ) : null}
    </>
  );
};
